import React, { useState } from "react";

import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SaveIcon from "@mui/icons-material/Save";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import { Application } from "src/domains/application/types";

import type { StackTab } from "../types";

import { EditableServicesSection } from "./EditableServicesSection";
import { PipelineModeSelector } from "./PipelineModeSelector";
import { VcsRepositorySection } from "./VcsRepositorySection";

// ── Types ─────────────────────────────────────────────────────────────────────

interface ConfigurationTabProps {
  system: Application;
  onDeleteProtectionChange: (enabled: boolean) => Promise<void>;
  deleteProtectionLoading: boolean;
  onAutoDeployChange: (enabled: boolean) => Promise<void>;
  autoDeployLoading: boolean;
  onRetryProvisioning?: () => Promise<void>;
  retryProvisioningLoading?: boolean;
  /** Switch to another stack-detail tab (used by the Shortcuts section). */
  onTabChange?: (tab: StackTab) => void;
  /** Save general settings (name and deploy mode). */
  onSaveGeneral?: (fields: {
    name: string;
    pipelineMode: string;
  }) => Promise<void>;
  saveGeneralLoading?: boolean;
}

type SettingsSection =
  "general" | "pipelineMode" | "vcs" | "services" | "shortcuts" | "danger";

const NAV_ITEMS: { id: SettingsSection; label: string }[] = [
  { id: "general", label: "General" },
  { id: "pipelineMode", label: "Pipeline Mode" },
  { id: "vcs", label: "VCS Repository" },
  { id: "services", label: "Detected Services" },
  { id: "shortcuts", label: "Shortcuts" },
  { id: "danger", label: "Danger Zone" },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

interface DetectedService {
  name: string;
  type: string;
  port?: number;
  dockerfilePath?: string;
  healthEndpoint?: string;
  internetFacing: boolean;
}

function getDetectedServices(system: Application): DetectedService[] {
  const config = system.stackConfig as Record<string, unknown> | undefined;
  if (!config) return [];
  const services = config._detectedServices as DetectedService[] | undefined;
  if (Array.isArray(services)) return services;
  return [];
}

function isAdopted(system: Application): boolean {
  const config = system.stackConfig as Record<string, unknown> | undefined;
  return config?._adopted === true;
}

// ── Section component ─────────────────────────────────────────────────────────

function SectionWrapper({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <Typography sx={{ fontSize: 18, fontWeight: 700, mb: 0.5 }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 2 }}>
        {subtitle}
      </Typography>
      <Box sx={{ mb: 3, borderBottom: 1, borderColor: "divider", pb: 3 }}>
        {children}
      </Box>
    </Box>
  );
}

function LabelValue({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        py: 1,
        gap: 2,
      }}
    >
      <Typography
        sx={{ fontSize: 13, fontWeight: 600, minWidth: 160, flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
        {value || "—"}
      </Typography>
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function ConfigurationTab({
  system,
  onDeleteProtectionChange,
  deleteProtectionLoading,
  onAutoDeployChange,
  autoDeployLoading = false,
  onRetryProvisioning,
  retryProvisioningLoading = false,
  onTabChange,
  onSaveGeneral,
  saveGeneralLoading = false,
}: ConfigurationTabProps): React.ReactElement {
  const [activeSection, setActiveSection] =
    useState<SettingsSection>("general");
  const detectedServices = getDetectedServices(system);
  const adopted = isAdopted(system);

  // ── General form state (matches mockup: name, alias, repo, deploy mode) ──
  const [generalName, setGeneralName] = useState(system.name);
  const [generalRepo] = useState(system.vcsRepoUrl ?? "");
  const [generalPipelineMode, setGeneralPipelineMode] = useState(
    system.pipelineMode ?? "rusha_managed",
  );
  const [saveGeneralError, setSaveGeneralError] = useState<string | null>(null);

  const handleSaveGeneral = async () => {
    if (!onSaveGeneral) return;
    setSaveGeneralError(null);
    try {
      await onSaveGeneral({
        name: generalName,
        pipelineMode: generalPipelineMode,
      });
    } catch (error) {
      setSaveGeneralError(
        error instanceof Error
          ? error.message
          : "Failed to save stack settings.",
      );
    }
  };

  return (
    <Box sx={{ display: "flex", gap: 4 }}>
      {/* Left sub-navigation */}
      <Box
        sx={{
          width: 200,
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 600,
            color: "text.secondary",
            mb: 1.5,
          }}
        >
          SETTINGS
        </Typography>
        {NAV_ITEMS.map((item) => (
          <Box
            key={item.id}
            onClick={() => setActiveSection(item.id)}
            sx={{
              py: 1,
              px: 2,
              borderRadius: 1,
              cursor: "pointer",
              fontSize: 13,
              fontWeight: activeSection === item.id ? 600 : 400,
              color:
                activeSection === item.id ? "primary.main" : "text.secondary",
              bgcolor:
                activeSection === item.id
                  ? "rgba(59,130,246,0.06)"
                  : "transparent",
              mb: 0.5,
              transition: "all 0.15s ease",
              "&:hover": {
                bgcolor: "rgba(59,130,246,0.04)",
              },
            }}
          >
            {item.label}
          </Box>
        ))}
      </Box>

      {/* Right content area */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* General section — matches mockup with editable fields */}
        {activeSection === "general" && (
          <SectionWrapper
            title="General"
            subtitle="Stack name, description, and basic configuration."
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Stack name
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={generalName}
                  onChange={(e) => setGeneralName(e.target.value)}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
                />
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Alias
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={system.alias ?? system.name}
                  disabled
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
                />
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Source repository
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={generalRepo}
                  disabled
                  helperText="Managed by the VCS connection"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
                />
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Deploy mode
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  select
                  SelectProps={{ native: true }}
                  value={generalPipelineMode}
                  onChange={(e) => setGeneralPipelineMode(e.target.value)}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
                >
                  <option value="rusha_managed">Rusha managed</option>
                  <option value="ci_build_rusha_deploy">CI managed</option>
                </TextField>
              </Box>

              <Box>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={
                    saveGeneralLoading ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <SaveIcon sx={{ fontSize: 16 }} />
                    )
                  }
                  onClick={handleSaveGeneral}
                  disabled={saveGeneralLoading || !onSaveGeneral}
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    fontSize: 13,
                    fontWeight: 500,
                  }}
                >
                  Save changes
                </Button>
              </Box>
              {saveGeneralError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {saveGeneralError}
                </Alert>
              )}

              {/* Read-only metadata (additional info beyond mockup) */}
              <Box
                sx={{
                  mt: 1,
                  pt: 2,
                  borderTop: 1,
                  borderColor: "divider",
                }}
              >
                <LabelValue label="Region" value={system.region} />
                <LabelValue
                  label="Stack Type"
                  value={
                    system.systemType === "data_engineering"
                      ? "Data Engineering"
                      : system.systemType === "analytical"
                        ? "Analytical"
                        : "Web Application"
                  }
                />
                <LabelValue
                  label="Created"
                  value={
                    system.createdAt
                      ? new Date(system.createdAt).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })
                      : "—"
                  }
                />
              </Box>
            </Box>
          </SectionWrapper>
        )}

        {/* Pipeline Mode section */}
        {activeSection === "pipelineMode" && (
          <SectionWrapper
            title="Pipeline Mode"
            subtitle="How Rusha builds and deploys your stack."
          >
            <PipelineModeSelector system={system} />
          </SectionWrapper>
        )}

        {/* VCS Repository section */}
        {activeSection === "vcs" && (
          <SectionWrapper
            title="VCS Repository"
            subtitle="Source code repository connection."
          >
            <VcsRepositorySection
              system={system}
              onRetryProvisioning={onRetryProvisioning}
              retryProvisioningLoading={retryProvisioningLoading}
            />
          </SectionWrapper>
        )}

        {/* Detected Services section */}
        {activeSection === "services" && (
          <SectionWrapper
            title="Detected Services"
            subtitle={
              adopted
                ? "Services detected during repo adoption."
                : "Services configured for this stack."
            }
          >
            {adopted ? (
              detectedServices.length > 0 ? (
                <Stack spacing={1.5}>
                  {detectedServices.map((svc) => (
                    <Paper
                      key={svc.name}
                      variant="outlined"
                      sx={{ p: 2, borderRadius: 1.5 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          mb: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "rgba(59,130,246,0.12)",
                            color: "primary.main",
                            fontSize: 13,
                            fontWeight: 700,
                          }}
                        >
                          {svc.name.charAt(0).toUpperCase()}
                        </Box>
                        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                          {svc.name}
                        </Typography>
                      </Box>
                      <LabelValue label="Type" value={svc.type} />
                      {svc.port && (
                        <LabelValue label="Port" value={`:${svc.port}`} />
                      )}
                      {svc.dockerfilePath && (
                        <LabelValue
                          label="Dockerfile"
                          value={svc.dockerfilePath}
                        />
                      )}
                      {svc.healthEndpoint && (
                        <LabelValue
                          label="Health endpoint"
                          value={svc.healthEndpoint}
                        />
                      )}
                      <LabelValue
                        label="Internet-facing"
                        value={svc.internetFacing ? "Yes" : "No"}
                      />
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <Typography
                  sx={{ color: "text.secondary", fontSize: 13, py: 2 }}
                >
                  No services were detected during adoption.
                </Typography>
              )
            ) : (
              <EditableServicesSection system={system} />
            )}
          </SectionWrapper>
        )}

        {/* Shortcuts section */}
        {activeSection === "shortcuts" && (
          <SectionWrapper
            title="Shortcuts"
            subtitle="Quick links to other stack tabs."
          >
            <Stack spacing={1}>
              {[
                {
                  tab: "secrets" as StackTab,
                  name: "Secrets",
                  desc: "Manage stack environment variables",
                },
                {
                  tab: "manifest" as StackTab,
                  name: "Manifest",
                  desc: "View rendered Kubernetes manifests",
                },
                {
                  tab: "costs" as StackTab,
                  name: "Costs",
                  desc: "View resource and deployment costs",
                },
              ].map((item) => (
                <Box
                  key={item.tab}
                  onClick={() => onTabChange?.(item.tab)}
                  role={onTabChange ? "button" : undefined}
                  tabIndex={onTabChange ? 0 : undefined}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 2,
                    borderRadius: 1.5,
                    border: 1,
                    borderColor: "divider",
                    cursor: onTabChange ? "pointer" : "default",
                    transition: "all 0.15s ease",
                    "&:hover": onTabChange
                      ? {
                          borderColor: "primary.main",
                          bgcolor: "rgba(59,130,246,0.04)",
                        }
                      : undefined,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                      {item.name}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                      {item.desc}
                    </Typography>
                  </Box>
                  <ChevronRightIcon
                    sx={{
                      fontSize: 18,
                      color: "text.secondary",
                      flexShrink: 0,
                    }}
                  />
                </Box>
              ))}
            </Stack>
          </SectionWrapper>
        )}

        {/* Danger Zone section */}
        {activeSection === "danger" && (
          <SectionWrapper
            title="Danger Zone"
            subtitle="Irreversible and destructive actions."
          >
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                borderColor: "error.main",
                borderWidth: 1,
                bgcolor: "rgba(239,68,68,0.04)",
              }}
            >
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: "error.main",
                  mb: 2,
                }}
              >
                Danger Zone
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  py: 1.5,
                  gap: 2,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                    Delete Protection
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                    When enabled, this stack cannot be deleted until protection
                    is turned off.
                  </Typography>
                </Box>
                <Switch
                  checked={system.deleteProtection ?? false}
                  onChange={(_, checked) =>
                    void onDeleteProtectionChange(checked)
                  }
                  disabled={deleteProtectionLoading}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  py: 1.5,
                  gap: 2,
                  borderTop: 1,
                  borderColor: "divider",
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                    Auto-Deploy
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                    When enabled, builds automatically trigger a deploy on
                    completion.
                  </Typography>
                </Box>
                <Switch
                  checked={system.autoDeploy ?? false}
                  onChange={(_, checked) => void onAutoDeployChange(checked)}
                  disabled={autoDeployLoading}
                />
              </Box>
            </Paper>
          </SectionWrapper>
        )}
      </Box>
    </Box>
  );
}

export default ConfigurationTab;
